import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RxCross2 } from "react-icons/rx";
import { getData } from "../lib/api";

/** Results panel for the header search box. */
export default function SearchResults({ open, query, onClose }) {
  const { t, i18n } = useTranslation();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !query || query.trim().length < 2) return;

    let cancelled = false;
    setLoading(true);

    getData("/search", { params: { q: query.trim(), lang: i18n.language } }).then((data) => {
      if (cancelled) return;
      setResults(data);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [open, query, i18n.language]);

  if (!open) return null;

  const groups = [
    { key: "projects", label: t("search.projects", "Projects"), base: "/agccProjects" },
    { key: "services", label: t("search.services", "Services"), base: "/agccServices" },
    { key: "news", label: t("search.news", "News & media"), base: "/agccNews" },
    { key: "pages", label: t("search.pages", "Pages"), base: "" },
  ];

  const total = groups.reduce((sum, group) => sum + (results?.[group.key]?.length || 0), 0);

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-8 w-full max-w-3xl rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-mstheme">
            {t("search.title", "Search results")}
            <span className="ml-2 text-sm font-normal text-gray-500">“{query}”</span>
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-gray-400 hover:text-gray-700">
            <RxCross2 size={22} />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
          {loading && <p className="py-8 text-center text-gray-500">{t("search.loading", "Searching…")}</p>}

          {!loading && (!results || total === 0) && (
            <p className="py-8 text-center text-gray-500">
              {t("search.empty", "Nothing matched your search.")}
            </p>
          )}

          {!loading &&
            results &&
            groups.map((group) => {
              const rows = results[group.key] || [];
              if (rows.length === 0) return null;

              return (
                <section key={group.key} className="mb-6">
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                    {group.label}
                  </h3>
                  <ul className="divide-y">
                    {rows.map((row) => (
                      <li key={`${group.key}-${row.id}`} className="py-2.5">
                        <Link to={group.base || "/"} onClick={onClose} className="block hover:text-mstheme">
                          <p className="font-medium text-gray-800">{row.title}</p>
                          {(row.short_description || row.excerpt || row.description) && (
                            <p className="line-clamp-2 text-sm text-gray-500">
                              {row.short_description || row.excerpt || row.description}
                            </p>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
        </div>
      </div>
    </div>
  );
}
